import React, { useState, useEffect, useRef } from 'react';
import './App.css';

function App() {
  const [currentSong, setCurrentSong] = useState(null);
  const [coverImage, setCoverImage] = useState('');
  const defaultCover = process.env.PUBLIC_URL + '/logo.png';

  // Riferimenti per il titolo e l'artista
  const titleRef = useRef(null);
  const artistRef = useRef(null);

  const [isTitleScrollable, setIsTitleScrollable] = useState(false);
  const [isArtistScrollable, setIsArtistScrollable] = useState(false);

  // Mappa dei programmi e relative copertine locali
  const programCovers = {
    'Pop Up Week End': process.env.PUBLIC_URL + '/program_covers/Pop Up Week End.jpg',
    'Spazi': process.env.PUBLIC_URL + '/program_covers/Spazi.jpg',
    'Notizie Flash': process.env.PUBLIC_URL + '/program_covers/Notizie Flash.png',
	'Hits Italian Week': process.env.PUBLIC_URL + '/Hits Italian Week.png',
	'The Billboard History': process.env.PUBLIC_URL + '/program_covers/The Billboard History.jpg',
	'AlmanH': process.env.PUBLIC_URL + '/program_covers/AlmanH.jpeg',
	'Chiacchericci': process.env.PUBLIC_URL + '/program_covers/Chiacchericci.jpeg',
	'LOST CITY': process.env.PUBLIC_URL + '/program_covers/LOST CITY.jpeg',
	'DJ KeySeven': process.env.PUBLIC_URL + '/program_covers/DJ KeySeven.JPG',
	'Top Of The Chart': process.env.PUBLIC_URL + '/program_covers/Top Of The Chart.png',
	'Curiosando tra le note': process.env.PUBLIC_URL + '/program_covers/CURIOSANDO TRA LE NOTE.jpeg',
	'Rock Wave': process.env.PUBLIC_URL + '/program_covers/Rock Wave.jpg',
	'Deep & Low': process.env.PUBLIC_URL + '/program_covers/Deep & Low.png',
	'SUPERMARKET': process.env.PUBLIC_URL + '/program_covers/SUPERMARKET.png',
	'MONDO PAZZO': process.env.PUBLIC_URL + '/program_covers/MONDO PAZZO.png',
	'Shadows Light': process.env.PUBLIC_URL + '/program_covers/Shadows Light.png',
	'GOLDENCLASSIC': process.env.PUBLIC_URL + '/program_covers/GOLDENCLASSIC.jpg',
	'MOROSITA': process.env.PUBLIC_URL + '/program_covers/MOROSITA.jpeg',
	'LORENZOSPEED': process.env.PUBLIC_URL + '/program_covers/LORENZOSPEED.jpeg',
	'TRANS EUROPE EXPRESS': process.env.PUBLIC_URL + '/program_covers/TRANS EUROPE EXPRESS.png',
        'MIXMAURO': process.env.PUBLIC_URL + '/program_covers/Mixmauro.jpg',
	'AllGOday': process.env.PUBLIC_URL + '/program_covers/AllGOday.jpg',
  };

  useEffect(() => {
    const fetchCurrentSong = async () => {
      try {
        const response = await fetch('https://api.laut.fm/station/meteomeano/current_song');
        const data = await response.json();
        console.log('Dati API laut.fm:', data);
        setCurrentSong(data);
      } catch (error) {
        console.error('Errore nel recupero del brano corrente:', error);
      }
    };

    fetchCurrentSong();
    const interval = setInterval(fetchCurrentSong, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Verifica se i titoli e l'artista devono scorrere
    if (titleRef.current) {
      setIsTitleScrollable(titleRef.current.scrollWidth > titleRef.current.clientWidth);
    }
    if (artistRef.current) {
      setIsArtistScrollable(artistRef.current.scrollWidth > artistRef.current.clientWidth);
    }
  }, [currentSong]);

  const getClosestProgramCover = (programTitle) => {
    if (!programTitle) return defaultCover;

    // Normalizza il titolo del programma
    const normalizedTitle = programTitle.toLowerCase().trim();

    // Cerca un match parziale nei titoli dei programmi
    for (const key in programCovers) {
      if (normalizedTitle.includes(key.toLowerCase())) {
        return programCovers[key];
      }
    }

    // Ritorna la copertina di default se non trova corrispondenze
    return defaultCover;
  };

  const fetchLastFmCover = async (artist, track) => {
    const apiKey = process.env.REACT_APP_LASTFM_API_KEY;
    const url = `https://ws.audioscrobbler.com/2.0/?method=track.getInfo&api_key=${apiKey}&artist=${encodeURIComponent(artist)}&track=${encodeURIComponent(track)}&format=json`;

    try {
      const response = await fetch(url);
      const data = await response.json();
      console.log('Dati API Last.fm:', data);

      if (data?.track?.album?.image) {
        const cover = data.track.album.image.find(img => img.size === 'extralarge');
        return cover ? cover['#text'] : null;
      }
    } catch (error) {
      console.error('Errore nella chiamata a Last.fm:', error);
    }
    return null;
  };

  useEffect(() => {
    const updateCoverImage = async () => {
      const programTitle = currentSong?.title || ''; // Prima riga
      const artistOrSecondLine = currentSong?.artist?.name || ''; // Seconda riga

      // Cerca la copertina per il programma
      const localCover = getClosestProgramCover(`${programTitle} ${artistOrSecondLine}`);
      if (localCover !== defaultCover) {
        setCoverImage(localCover);
        return;
      }

      // Se non trova una copertina locale, utilizza Last.fm
      if (currentSong?.artist?.name && currentSong?.title) {
        const cover = await fetchLastFmCover(currentSong.artist.name, currentSong.title);
        setCoverImage(cover || defaultCover);
      } else {
        setCoverImage(defaultCover);
      }
    };

    updateCoverImage();
  }, [currentSong]);

  const getDisplayText = () => {
    if (currentSong?.title && currentSong?.artist?.name) {
      return `${currentSong.title} - ${currentSong.artist.name}`;
    } else if (currentSong?.title) {
      return currentSong.title;
    } else {
      return 'Radio Meano';
    }
  };

  useEffect(() => {
    const audioElement = document.querySelector('audio');
    if (audioElement) {
      audioElement.setAttribute('title', getDisplayText());
    }
  }, [currentSong]);

  return (
    <div className="App">
      <header>
        <h1>Radio Meano</h1>
      </header>
      <main>
        {currentSong ? (
          <div className="song-info">
            <img
              src={coverImage}
              alt={currentSong.title || 'Radio Meano'}
              className="cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = defaultCover;
              }}
            />
            <div
              className={`marquee-container ${isTitleScrollable ? 'scrollable' : ''}`}
              ref={titleRef}
            >
              <span>{currentSong.title || 'Ora in onda'}</span>
            </div>
            <div
              className={`marquee-container ${isArtistScrollable ? 'scrollable' : ''}`}
              ref={artistRef}
            >
              <span>{currentSong.artist?.name || 'Artista sconosciuto'}</span>
            </div>
          </div>
        ) : (
          <p>Caricamento in corso...</p>
        )}
        <audio
          controls
          src="https://stream.laut.fm/meteomeano"
          title={getDisplayText()}
        ></audio>
      </main>
      <footer>
        <p>&copy; 2024 Radio Meano. Tutti i diritti riservati.</p>
      </footer>
    </div>
  );
}

export default App;
